import {
  initAll,
  identify,
  setUserId,
  track,
  add,
  remove,
  Identify,
  type UnifiedOptions,
} from '@amplitude/unified'
import {
  sessionReplayPlugin,
  SessionReplayPlugin,
} from '@amplitude/plugin-session-replay-browser'
import { AmplitudeUser } from '@/types/AmplitudeUser'
import accountStore from '@/stores/account.store'
import authStore from '@/stores/auth.store'
import unitStore from '@/stores/unit.store'
import { ConsentAwareContract } from '@/services/contracts/consent-aware.contract'
import usercentricsService from '@/services/usercentrics/usercentrics.service'
import apiService from '@/services/api.service'
import { ConsentType } from '@/enums'
import languageLevelMapper from '@/services/amplitude/language-level-mapper.service'
import { EventPayload } from '@/services/amplitude/interfaces/event-payload.interface'
import EventPayloadFactory from '@/services/amplitude/event-payload.factory'
import UniversityEntranceQualificationMapper from '@/services/amplitude/university-entrance-qualification-mapper.service'
import amplitudeExperimentService from '@/services/amplitude/amplitude-experiment.service'

/**
 * Amplitude integration built on the Browser Unified SDK
 * (`@amplitude/unified`). A single `initAll` call wires Analytics, Session
 * Replay and Feature Experiment onto one instance, so all three share the same
 * user identity (set via {@link setUserId}). Events go straight to Amplitude —
 * the frontend no longer routes through the consent proxy. User-id hashing is
 * computed once by the backend and delivered as `account.amplitudeUserId` —
 * see `docs/AMPLITUDE-USER-ID-HASHING.md`.
 *
 * Identity & consent model (Unified User Hashing):
 * - The user id is ALWAYS hashed (regardless of consent) and direct PII (email,
 *   name) is never sent — country/city are kept for geographic segmentation — so
 *   a user keeps one continuous profile across consent changes and across
 *   frontend/backend events. The hashed id is the single source of truth used by
 *   Analytics, Session Replay AND Experiment.
 * - Consent only unlocks Session Replay and attaches the unhashed UUID as the
 *   `trueUserId` user property (for export / CRM lookup).
 */
class AmplitudeService implements ConsentAwareContract {
  // The Amplitude Browser SDK persists unsent events in localStorage under
  // `AMP_unsent_<first 10 chars of api key>`. A single failed flush (network
  // blip, 5xx, offline) can poison this queue: the SDK keeps retrying the
  // stale batch on every load and never makes progress, so ALL subsequent
  // events pile up and are silently never delivered.
  private static readonly UNSENT_QUEUE_KEY_PREFIX = 'AMP_unsent_'

  // Events still queued after this long are treated as undeliverable and
  // dropped so they stop blocking delivery of newer events. They were never
  // going to send anyway; dropping them unblocks all future tracking.
  private static readonly MAX_QUEUE_AGE_MS = 24 * 60 * 60 * 1000 // 24h

  private consent = false
  private initialized = false
  private sessionReplayEnabled = false

  constructor() {
    this.setupEventListeners()
  }

  public async activate(): Promise<void> {
    if (!usercentricsService.hasUserInteractedWithUsercentrics()) return
    await this.applyConsent(true)
  }

  public async deactivate(): Promise<void> {
    if (!usercentricsService.hasUserInteractedWithUsercentrics()) return
    await this.applyConsent(false)
  }

  /**
   * Applies the current consent state. Amplitude is always initialized and
   * tracking (with a hashed user id); consent only toggles Session Replay and
   * the `trueUserId` user property.
   */
  private async applyConsent(consent: boolean): Promise<void> {
    this.consent = consent
    this.saveConsentForAmplitude()

    await this.initializeAmplitude()
    if (!this.initialized) return

    if (this.consent) {
      await this.enableSessionReplay()
    } else {
      await this.disableSessionReplay()
    }

    this.identifyUser(this.getAmplitudeUserData())
  }

  private setupEventListeners() {
    window.addEventListener('account-data-saved', ((event: CustomEvent) => {
      this.trackEvents(event.detail.changedData)
    }) as EventListener)
  }

  private saveConsentForAmplitude(): void {
    // Runs in the background on every page load once the user has interacted
    // with the consent banner — also on public pages like /application where
    // no session exists. Without a token the backend cannot associate the
    // consent, and the request must never trigger the global 401 redirect to
    // the Auth0 login (or the maintenance screen).
    if (!authStore.state.token) return

    apiService.account
      .putConsents(
        [
          {
            type: ConsentType.AMPLITUDE,
            hasOptedIn: this.consent,
          },
        ],
        { skipGlobalErrorHandling: true },
      )
      .catch((error) => {
        console.error('Failed to update consents:', error)
      })
  }

  private async initializeAmplitude(): Promise<void> {
    if (this.initialized) return

    try {
      await this.waitForAccount(3000)
      const accountData = accountStore.state.account

      if (!accountData?.uuid) {
        console.warn('Amplitude init: No valid UUID found in account data')
        return
      }

      if (!accountData.amplitudeUserId) {
        console.warn(
          'Amplitude init: No hashed Amplitude user id found in account data',
        )
        return
      }

      const apiKey = this.getAmplitudeApiKey()
      if (!apiKey) {
        console.warn('Amplitude init: VITE_AMPLITUDE_API_KEY is not configured')
        return
      }

      const hashedUserId = accountData.amplitudeUserId

      this.pruneStaleUnsentEvents()
      // Pre-migration browsers still have the raw UUID in AMP_* identity
      // storage. initAll() reads that synchronously and stamps session_start +
      // Page Viewed before setUserId() can run — so pass the hash into init
      // (options.userId wins over localStorage) and rewrite storage first so
      // those autocapture events never leave with the raw UUID.
      this.migrateStoredUserId(apiKey, hashedUserId)

      await initAll(apiKey, this.unifiedOptions(hashedUserId))
      this.initialized = true

      // initAll always registers the Session Replay plugin; we gate it on
      // consent ourselves, so drop the auto-registered one and (re-)add it only
      // when consent is granted.
      await remove(SessionReplayPlugin.pluginName).promise
      this.sessionReplayEnabled = false

      setUserId(hashedUserId)
      this.identifyUser(this.getAmplitudeUserData())

      await amplitudeExperimentService.initialize()
    } catch (e) {
      console.warn(
        'Amplitude init: account data not loaded or initialization failed',
        e,
      )
    }
  }

  /**
   * Rewrites a leftover raw UUID in the SDK identity blob to the hashed id
   * before {@link initAll}. Key format matches the Browser SDK
   * (`AMP_` + first 10 chars of the API key). Best-effort: malformed storage
   * is left untouched so init can still proceed.
   */
  private migrateStoredUserId(apiKey: string, hashedUserId: string): void {
    try {
      const key = `AMP_${apiKey.substring(0, 10)}`
      const raw = localStorage.getItem(key)
      if (!raw) return

      const identity = JSON.parse(raw) as { userId?: string }
      if (!identity || typeof identity !== 'object') return
      if (identity.userId === hashedUserId) return

      identity.userId = hashedUserId
      localStorage.setItem(key, JSON.stringify(identity))
    } catch (e) {
      console.warn('Amplitude: failed to migrate stored user id', e)
    }
  }

  /**
   * Drops stale events from the SDK's localStorage upload queue before init.
   *
   * A failed flush leaves events in `AMP_unsent_*`; the SDK has no recovery
   * mechanism and keeps retrying the stale batch, permanently blocking delivery
   * of all newer events. Removing events older than MAX_QUEUE_AGE_MS unblocks
   * the queue. Best-effort: the queue is an SDK-internal implementation detail,
   * so any parsing/storage error is swallowed rather than breaking init.
   */
  private pruneStaleUnsentEvents(): void {
    try {
      const now = Date.now()

      Object.keys(localStorage)
        .filter((key) =>
          key.startsWith(AmplitudeService.UNSENT_QUEUE_KEY_PREFIX),
        )
        .forEach((key) => {
          const raw = localStorage.getItem(key)
          if (!raw) return

          const events = JSON.parse(raw)
          if (!Array.isArray(events) || events.length === 0) return

          const freshEvents = events.filter(
            (event) =>
              typeof event?.time !== 'number' ||
              now - event.time < AmplitudeService.MAX_QUEUE_AGE_MS,
          )

          if (freshEvents.length === 0) {
            localStorage.removeItem(key)
          } else if (freshEvents.length < events.length) {
            localStorage.setItem(key, JSON.stringify(freshEvents))
          }
        })
    } catch (e) {
      console.warn('Amplitude: failed to prune stale unsent event queue', e)
    }
  }

  private unifiedOptions(hashedUserId: string): UnifiedOptions {
    return {
      serverZone: 'EU',
      // https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2#configure-the-sdk
      analytics: {
        // Must be set at init time — session_start / Page Viewed fire inside
        // initAll() before a later setUserId() call can take effect.
        userId: hashedUserId,
        // Give up on a persistently failing batch within the session instead of
        // letting it accumulate in the localStorage queue indefinitely (default
        // is 12). Combined with pruneStaleUnsentEvents() this prevents a poisoned
        // queue from silently blocking all tracking.
        flushMaxRetries: 5,
        // https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2#autocapture
        autocapture: {
          attribution: false,
          pageViews: true,
          sessions: true,
          formInteractions: true,
          fileDownloads: true,
          elementInteractions: true,
        },
        // https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2#optional-tracking
        trackingOptions: {
          ipAddress: true,
          language: true,
          platform: true,
        },
        // https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2#disable-cookies
        identityStorage: 'localStorage',
      },
      // Registered inert; the real (0.3) plugin is added on consent. Sample rate
      // is overwritten by the remote config in the Amplitude dashboard.
      sessionReplay: {
        sampleRate: 0,
      },
      // Fetch is triggered explicitly by the experiment service once the user id
      // is set, so assignment always lands on the current user's profile.
      experiment: {
        deploymentKey: import.meta.env.VITE_AMPLITUDE_EXPERIMENT_DEPLOYMENT_KEY,
        fetchOnStart: false,
      },
      // Guides & Surveys is unused — skip loading it to avoid extra storage/network.
      engagement: {
        skip: true,
      },
    }
  }

  private async enableSessionReplay(): Promise<void> {
    if (this.sessionReplayEnabled) return
    // 30% is a fallback; the remote config in the Amplitude dashboard overrides it.
    await add(sessionReplayPlugin({ sampleRate: 0.3 })).promise
    this.sessionReplayEnabled = true
  }

  private async disableSessionReplay(): Promise<void> {
    if (!this.sessionReplayEnabled) return
    await remove(SessionReplayPlugin.pluginName).promise
    this.sessionReplayEnabled = false
  }

  private getAmplitudeApiKey() {
    return import.meta.env.VITE_AMPLITUDE_API_KEY
  }

  private waitForAccount(timeoutMs = 3000, intervalMs = 100): Promise<void> {
    return new Promise((resolve, reject) => {
      const start = Date.now()
      const interval = setInterval(() => {
        const uuid = accountStore.state.account?.uuid
        if (uuid) {
          clearInterval(interval)
          resolve()
        } else if (Date.now() - start > timeoutMs) {
          clearInterval(interval)
          reject(new Error('Timeout waiting for account data'))
        }
      }, intervalMs)
    })
  }

  private calculateAge(birthday: string): number | null {
    if (!birthday) return null

    const birthDate = new Date(birthday)
    const today = new Date()
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--
    }

    return age
  }

  private getAmplitudeUserData(): AmplitudeUser {
    const accountData = accountStore.state.account
    const activeApplication = unitStore.getActiveApplication()

    const germanLevel =
      accountData?.languageSkills?.filter((lang) => lang.code === 'de')[0]
        ?.level || null

    // PII like email and name is intentionally never sent. Country and city are
    // kept for geographic segmentation (in addition to `isInternational`).
    return {
      createdAt: accountData?.createdAt || '',
      campus: activeApplication?.campus?.name || null,
      startDate: activeApplication?.intake?.date || null,
      course: activeApplication?.course?.name || null,
      ageAtApplication: accountData?.birthday
        ? this.calculateAge(accountData.birthday)
        : null,
      country: accountData?.shippingCountry || null,
      city: accountData?.shippingCity || null,
      isInternational:
        accountStore.isDachRegion() !== null
          ? !accountStore.isDachRegion()
          : null,
      hzb_type: UniversityEntranceQualificationMapper.map(
        accountData?.universityEntranceQualification,
        accountData?.universityEntranceQualificationBw,
      ),
      germanLevel: languageLevelMapper.mapLevel(germanLevel),
    } as AmplitudeUser
  }

  private identifyUser(user: AmplitudeUser) {
    const identifyEvent = new Identify()

    Object.entries(user).forEach(([key, value]) => {
      if (value !== null) {
        identifyEvent.set(key as keyof AmplitudeUser, value)
      }
    })

    identifyEvent.set('consentFunctional', this.consent)

    // The unhashed UUID is attached only with consent, so consented users can be
    // exported / matched in the CRM. Without consent it is removed.
    const rawUserId = accountStore.state.account?.uuid
    if (this.consent && rawUserId) {
      identifyEvent.set('trueUserId', rawUserId)
    } else {
      identifyEvent.unset('trueUserId')
    }

    identify(identifyEvent)
  }

  public trackEvent(eventPayload: EventPayload) {
    if (!eventPayload || !eventPayload.eventProperties) {
      console.warn('AmplitudeService.trackEvent: Invalid eventPayload provided')
      return
    }

    track(eventPayload.eventType, eventPayload.eventProperties)
  }

  private trackEvents(changedData: Record<string, unknown>) {
    if (!this.initialized) return

    Object.entries(changedData).forEach(([key]) => {
      const payload = EventPayloadFactory.buildPayload(key, changedData)
      if (payload) {
        this.trackEvent(payload)
      }
    })
    this.identifyUser(this.getAmplitudeUserData())
  }
}

export default new AmplitudeService()
